import isNumber from "is-number";
import _ from "lodash";
import { action, autorun, computed, IObservableValue, observable } from "mobx";
import queryString from "qs";
import React, { useEffect, useMemo } from "react";
import { useQueryClient } from "react-query";
import { CandidateGetAppliedPositionsResponse } from "../../api/jobs/candidateGetAppliedPositions";
import { CvPortalJobFull } from "../../api/jobs/getJobFull";
import { wt_rekruttering, wt_tilbud } from "../../WebtempTyper";
import { getLocStore } from "../../wsLocalization";
import { LocContext } from "../../wsLocalization/LocContext";
import { storageArea } from "../../WSUtils/storageArea";
import { Store, StoreStandardParams } from "../../WSUtils/Store";
import { useLatestQuery } from "../../WSUtils/useLatestQuery";
import { UserInfo } from "../../WSUtils/UserInfo";
import { useStore } from "../../WSUtils/useStore";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../WSUtils/wsQueryStringStore";
import { routerStore } from "../../WSUtils/WSRouter";
import { sentryAssertData } from "../../WSUtils/wsSentry";
import { useCvPortalJobFull } from "../JobView/useCvPortalJobFull";
import { CVEditStepType, Step } from "../Steps/Step";

const _applyingForJob = () => {
  const t = queryStringStore.qs[CVPSearchParams.cvpt]?.toString();
  const r = queryStringStore.qs[CVPSearchParams.cvpr]?.toString();

  const id_tilbud = isNumber(t) ? Number(t) : null;
  const id_rekruttering = isNumber(r) ? Number(r) : null;

  let irel: number;
  let trel: number;

  if (id_tilbud) {
    irel = id_tilbud;
    trel = wt_tilbud;
  } else if (id_rekruttering) {
    irel = id_rekruttering;
    trel = wt_rekruttering;
  }

  const correctPath = routerStore.location.pathname.match(/apply(.aspx)?$/i);

  if (!correctPath || !trel || !irel) return { irel: null, trel: null };

  return {
    trel,
    irel,
  };
};

export const CVEditContext = React.createContext<CVEditContextStore>(null);

export const useCVEditContext = (
  userInfo: UserInfo,
  layoutClassName: IObservableValue<string>
) => {
  const queryClient = useQueryClient();
  const candidateAPI = userInfo.fullAPI.candidateAPI;

  const dataFromServer = useMemo(
    () =>
      observable<CVEditContextStoreInitialData>({
        //// IMPORTANT! ensure all references are also present in the initialized check below

        job: null,
        appliedPositions: null,
        //// IMPORTANT! ensure all references are also present in the initialized check below
        get initialized() {
          const t: CVEditContextStoreInitialData = this;

          return !!(
            (t.job || !enableUseCvPortalJobFull()) &&
            (t.appliedPositions || !enableFetchAppliedPositions())
          );
        },
      }),
    []
  );

  const { irel, trel } = _applyingForJob();
  const isApplying = !!irel && !!trel;

  const observableContext = useStore(
    "CVEditContextStore",
    (dt) =>
      new CVEditContextStore({
        name: "CVEditContextStore",
        getUserInfo: () => userInfo,
        getQueryClient: () => queryClient,
        getDataFromServer: () => dataFromServer,
        parentDt: dt,
        _layoutClassName: layoutClassName,
        _applyingForJob: _applyingForJob,
      })
  );

  const { data: details } = useLatestQuery(
    ["candidateAPI.getDetailsForCandidate"],
    () =>
      candidateAPI.getDetailsForCandidate({
        userInfo,
      })
  );

  const enableUseCvPortalJobFull = () => isApplying;

  const { data: job } = useCvPortalJobFull(
    {
      idRelated: irel,
      typeRelated: trel,
    },
    { enabled: enableUseCvPortalJobFull() }
  );

  const enableFetchAppliedPositions = () =>
    !!userInfo.idFirmCandidate && isApplying;

  const { data: appliedPositions } = useLatestQuery(
    ["candidateGetAppliedPositions", userInfo.idCandidate],
    () =>
      userInfo.fullAPI.jobsAPI.candidateGetAppliedPositions({
        idConcern: userInfo.idConcern,
        idCandidate: userInfo.idCandidate,
      }),
    { enabled: enableFetchAppliedPositions() }
  );

  const backendResponses = [details, job, appliedPositions];

  sentryAssertData(backendResponses, {
    userInfo,
  });

  // put react-query data in mobx state
  useEffect(
    action(() => {
      //// IMPORTANT! ensure all references are also present in useEffect array below!

      dataFromServer.job = job;
      dataFromServer.appliedPositions = appliedPositions;
      //// IMPORTANT! ensure all references are also present in useEffect array below!
    }),
    backendResponses
  );

  return observableContext;
};

export interface CVEditContextStoreInitialData {
  job: CvPortalJobFull;
  appliedPositions: CandidateGetAppliedPositionsResponse;

  readonly initialized: boolean;
}

export interface CVEditContextStoreParams extends StoreStandardParams {
  getDataFromServer: () => CVEditContextStoreInitialData;
  _layoutClassName: IObservableValue<string>;
  _applyingForJob: () => { irel: number; trel: number };
}

export class CVEditContextStore extends Store {
  declare _layoutClassName: IObservableValue<string>;
  declare _applyingForJob: () => { irel: number; trel: number };
  declare getDataFromServer: () => CVEditContextStoreInitialData;

  @computed get layoutClassName() {
    return this._layoutClassName.get();
  }

  @computed get jobApplicationMode() {
    return !!this.applyingForJob?.irel && !!this.applyingForJob?.trel;
  }

  @computed get applyingForJob() {
    return this._applyingForJob();
  }

  @computed get isApplyingForAd() {
    return !!this.applyingForJob?.irel && !!this.applyingForJob?.trel;
  }

  @computed get applyingForAdUrl() {
    let qs: any = { k: this.userInfo.idConcern };

    const { trel, irel } = this.applyingForJob;

    if (!irel || !trel) return null;

    if (trel === wt_rekruttering) qs[CVPSearchParams.cvpr] = irel;
    if (trel === wt_tilbud) qs[CVPSearchParams.cvpt] = irel;

    return `job?${queryString.stringify(qs)}`;
  }

  @computed get alreadyAppliedToPosition() {
    if (!this.isApplyingForAd) return false;
    if (!this.dataFromServer.initialized) return false;

    const { trel, irel } = this.applyingForJob;
    return !!this.dataFromServer.appliedPositions?.items?.includes(
      `${trel}:${irel}`
    );
  }

  @computed get appliedToPositionThisSession() {
    if (!this.isApplyingForAd) return false;
    if (!this.dataFromServer.initialized) return false;

    const { trel, irel } = this.applyingForJob;
    return !!this.dataFromServer.appliedPositions?.appliedToThisSession?.includes(
      `${trel}:${irel}`
    );
  }

  @computed get dataFromServer() {
    return this.getDataFromServer();
  }

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  @computed get steps(): Step[] {
    const steps = [
      {
        label: this.L.id("5f2dea2dfd0a").loc("Detaljer"),
        value: CVEditStepType.Details,
      },
      {
        label: this.L.id("fb5f6147c8b4").loc("Praksis"),
        value: CVEditStepType.Experience,
      },
      {
        label: this.L.id("d2e215184cb1").loc("Kvalifikasjoner"),
        value: CVEditStepType.Qualifications,
      },
      {
        label: this.jobApplicationMode
          ? this.L.id("f8a3a69b19c9").loc("Vedlegg")
          : this.L.id("3b9ae7569a0a").loc("Dokumenter"),
        value: CVEditStepType.Documents,
      },
    ];

    if (this.jobApplicationMode) {
      steps.push({
        label: this.L.id("7a79c7ca8eea").loc("Spørreskjema"),
        value: CVEditStepType.QuestionForm,
      });
    }

    if (this.jobApplicationMode) {
      steps.push({
        label: this.L.id("bf084b223e36").loc("Søknad"),
        value: CVEditStepType.Application,
      });
    }

    return steps;
  }

  previouslyViewedStepForThisAdApplication = (() => {
    if (!this.isApplyingForAd) return null;

    this.dt.disposers.push(
      autorun(() => {
        if (this.isApplyingForAd)
          storageArea.setItem(
            "previouslyViewedApplicationStep",
            JSON.stringify({
              irel: this.applyingForJob?.irel,
              trel: this.applyingForJob?.trel,
              stepNumber: this.currentStepNumber,
            })
          );
      })
    );

    const lastViewed = JSON.parse(
      storageArea.getItem("previouslyViewedApplicationStep") ?? "null"
    );

    const isSameJob =
      lastViewed?.irel === this.applyingForJob.irel &&
      lastViewed?.trel === this.applyingForJob.trel;

    if (isSameJob && isNumber(lastViewed?.stepNumber)) {
      return Number(lastViewed.stepNumber);
    }

    return null;
  })();

  @computed get currentStep(): Step {
    return this.steps[this.currentStepNumber - 1];
  }

  @computed get currentStepNumber(): number {
    let stepNr = -1;

    if (isNumber(queryStringStore.qs[CVPSearchParams.cvpstep])) {
      stepNr = Number(queryStringStore.qs[CVPSearchParams.cvpstep]);
    }

    if (
      stepNr === -1 &&
      this.applyingForJob &&
      typeof this.previouslyViewedStepForThisAdApplication === "number"
    ) {
      stepNr = this.previouslyViewedStepForThisAdApplication;
    }

    return _.clamp(stepNr, 1, this.steps.length);
  }

  @computed get showApplyForPositionButton() {
    if (!this.jobApplicationMode) return false;

    return !this.showNextStepButton;
  }

  @computed get showNextStepButton() {
    const showingExperienceEditor = false;
    /*this.currentStep.value === CVEditStepType.Experience &&
      this.experienceStepStore?.experienceSegmentStore?.showEditor;*/

    if (showingExperienceEditor) return false;

    const showingDocumentSegmentEditor = false;
    /*this.currentStep.value === CVEditStepType.Documents &&
    this._documentStepStore?.documentSegmentStore?.showEditor*/
    if (showingDocumentSegmentEditor) return false;

    const showingAttachmentSegmentEditor = false;
    /*this.currentStep.value === CVEditStepType.Documents &&
      this._documentStepStore?.attachmentsSegmentStore?.showEditor*/
    if (showingAttachmentSegmentEditor) return false;

    const showingAttachmentPicker = false;
    /*this.currentStep.value === CVEditStepType.Documents &&
    this._documentStepStore?.attachmentsSegmentStore?.showAttachmentPicker*/
    if (showingAttachmentPicker) return false;

    if (this.currentStepNumber === this.steps.length) return false;

    return true;
  }
}
